import { inject, Injectable, signal } from '@angular/core';
import { User } from '../../../../../models/user.model';
import { Router } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { Observable, BehaviorSubject, throwError, from } from 'rxjs';
import { catchError, tap, switchMap, filter, take } from 'rxjs/operators';
import { EncryptionService } from '../../../../../services/encryption.service';
import { AuthUrls } from '../../../../enums/api-urls';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private currentUser = signal<User | null>(null);
  public readonly user = this.currentUser.asReadonly();
  private router = inject(Router);
  private http = inject(HttpClient);
  private encryptionService = inject(EncryptionService);
  
  // Refresh token state management
  private refreshTokenInProgress = false;
  private refreshTokenSubject = new BehaviorSubject<string | null>(null);
  private refreshTokenUrl = AuthUrls.REFRESH_TOKEN;

  constructor() {
    // Check if user is already logged in (e.g., from localStorage)
    // In a real app, you would check tokens/session here
    const savedUser = localStorage.getItem('currentUser');
    if (savedUser) {
      try {
        this.currentUser.set(JSON.parse(savedUser));
      } catch (e) {
        localStorage.removeItem('currentUser');
      }
    }
  }

  login(identifier: string, password: string, identifierType: 'email' | 'mobile' = 'email'): boolean {
    // Valid credentials
 
    const VALID_MOBILE = '9822813504';
    const VALID_PASSWORD = 'Hcms@1234';

    // Validate credentials
    let isValid = false;
 
  
      // Check mobile login - remove any spaces, dashes, or country code prefix
      const cleanMobile = identifier.replace(/[\s\-+]/g, '').trim();
      isValid = cleanMobile === VALID_MOBILE && password === VALID_PASSWORD;


    if (isValid) {
      const authenticatedUser: User = {
        id: '1',
        firstName: 'Admin',
        lastName: 'User',
        phone: VALID_MOBILE,
        role: 'Doctor',
        isActive: true,
        createdAt: new Date()
      };
      this.currentUser.set(authenticatedUser);
      // Save to localStorage for persistence
      localStorage.setItem('currentUser', JSON.stringify(authenticatedUser));
      return true;
    }
    
    return false;
  }

  // Legacy method for backward compatibility
  loginWithEmail(email: string, password: string): boolean {
    return this.login(email, password, 'email');
  }

  loginWithMobile(mobile: string, password: string): boolean {
    return this.login(mobile, password, 'mobile');
  }

  logout(): void {
    this.currentUser.set(null);
    localStorage.removeItem('currentUser');
    localStorage.removeItem('authToken');
    localStorage.removeItem('refreshToken');
    this.refreshTokenSubject.next(null);
    this.refreshTokenInProgress = false;
    this.router.navigate(['/login']);
  }

  isAuthenticated(): boolean {
    return this.currentUser() !== null;
  }

  hasRole(role: string): boolean {
    return this.currentUser()?.role === role;
  }

  /**
   * Set the current user (typically called after successful authentication)
   * @param user - The authenticated user object
   */
  setUser(user: User): void {
    this.currentUser.set(user);
    localStorage.setItem('currentUser', JSON.stringify(user));
  }

  // Token management methods with encryption
  async setToken(token: string): Promise<void> {
    try {
      const encryptedToken = await this.encryptionService.encrypt(token);
      localStorage.setItem('authToken', encryptedToken);
    } catch (error) {
      console.error('Failed to encrypt token:', error);
      // Fallback to unencrypted storage if encryption fails
      localStorage.setItem('authToken', token);
    }
  }

  async getToken(): Promise<string | null> {
    const encryptedToken = localStorage.getItem('authToken');
    if (!encryptedToken) {
      return null;
    }
    
    try {
      return await this.encryptionService.decrypt(encryptedToken);
    } catch (error) {
      console.error('Failed to decrypt token:', error);
      // If decryption fails, try to read as plain text (for backward compatibility)
      return encryptedToken;
    }
  }

  removeToken(): void {
    localStorage.removeItem('authToken');
  }

  // Refresh token management methods with encryption
  async setRefreshToken(refreshToken: string): Promise<void> {
    try {
      const encryptedRefreshToken = await this.encryptionService.encrypt(refreshToken);
      localStorage.setItem('refreshToken', encryptedRefreshToken);
    } catch (error) {
      console.error('Failed to encrypt refresh token:', error);
      // Fallback to unencrypted storage if encryption fails
      localStorage.setItem('refreshToken', refreshToken);
    }
  }

  async getRefreshToken(): Promise<string | null> {
    const encryptedRefreshToken = localStorage.getItem('refreshToken');
    if (!encryptedRefreshToken) {
      return null;
    }
    
    try {
      return await this.encryptionService.decrypt(encryptedRefreshToken);
    } catch (error) {
      console.error('Failed to decrypt refresh token:', error);
      // If decryption fails, try to read as plain text (for backward compatibility)
      return encryptedRefreshToken;
    }
  }

  removeRefreshToken(): void {
    localStorage.removeItem('refreshToken');
  }

  // Set both tokens (typically called after login)
  async setTokens(accessToken: string, refreshToken: string): Promise<void> {
    await Promise.all([
      this.setToken(accessToken),
      this.setRefreshToken(refreshToken)
    ]);
  }

  /**
   * Refresh the access token using the expired access token
   * Returns an Observable that emits the new access token
   * The payload includes the expired access token (required by backend)
   * @param expiredToken - The expired access token to be sent in the payload
   */
  refreshAccessToken(expiredToken?: string | null): Observable<string> {
    // If refresh is already in progress, return the subject's observable
    if (this.refreshTokenInProgress) {
      return this.refreshTokenSubject.pipe(
        filter(token => token !== null),
        take(1),
        switchMap(token => {
          if (token) {
            return new Observable<string>(observer => {
              observer.next(token);
              observer.complete();
            });
          }
          return throwError(() => new Error('Token refresh failed'));
        })
      );
    }

    this.refreshTokenInProgress = true;
    this.refreshTokenSubject.next(null);


    // Get the expired token if not provided
    const tokenToUse = expiredToken;
    
    if (!tokenToUse) {
      // Try to get the expired token from storage
      return from(this.getToken()).pipe(
        switchMap(storedToken => {
          if (!storedToken) {
            this.refreshTokenInProgress = false;
            console.error('❌ No expired token available for refresh');
            return throwError(() => new Error('No expired token available'));
          }
          return this.prepareAndCallRefreshAPI(storedToken);
        })
      );
    } else {
      return this.prepareAndCallRefreshAPI(tokenToUse);
    }
  }

  /**
   * Prepare the refresh token API payload and call the API
   */
  private prepareAndCallRefreshAPI(expiredToken: string): Observable<string> {
    
    // Prepare payload with expired token
    // Backend expects the expired access token in the payload as "token"
    const payload: any = {
      token: expiredToken
    };

    // Optionally include refresh token if available
    return from(this.getRefreshToken()).pipe(
      switchMap(refreshToken => {
        if (refreshToken) {
          payload.refreshToken = refreshToken;
        }
        
        // Send payload directly with token field (no encryption wrapper)
        console.log('✅ Calling refresh API with token in payload...');
        return this.callRefreshTokenAPI(payload);
      }),
      catchError(getRefreshTokenError => {
        console.warn('⚠️ Error getting refresh token, calling API with expired token only...');
        // Even if getting refresh token fails, call API with expired token
        return this.callRefreshTokenAPI(payload);
      })
    );
  }

  /**
   * Internal method to call the refresh token API
   */
  private callRefreshTokenAPI(payload: any): Observable<string> {
    console.log('🌐 Calling refresh token API:', this.refreshTokenUrl);
    return this.http.post<{ accessToken: string; refreshToken?: string }>(this.refreshTokenUrl, payload).pipe(
      switchMap(response => {
        console.log('✅ Refresh token API success, updating tokens...');
        // Save the new access token
        return from(this.setToken(response.accessToken)).pipe(
          switchMap(() => {
            // Update refresh token if a new one is provided
            if (response.refreshToken) {
              return from(this.setRefreshToken(response.refreshToken)).pipe(
                switchMap(() => {
                  this.refreshTokenInProgress = false;
                  this.refreshTokenSubject.next(response.accessToken);
                  console.log('✅ Token refresh completed successfully');
                  return new Observable<string>(observer => {
                    observer.next(response.accessToken);
                    observer.complete();
                  });
                })
              );
            } else {
              this.refreshTokenInProgress = false;
              this.refreshTokenSubject.next(response.accessToken);
              console.log('✅ Token refresh completed successfully (no new refresh token)');
              return new Observable<string>(observer => {
                observer.next(response.accessToken);
                observer.complete();
              });
            }
          })
        );
      }),
      catchError(error => {
        this.refreshTokenInProgress = false;
        this.refreshTokenSubject.next(null);
        console.error('❌ Token refresh failed:', error);
        
        // If refresh fails, logout the user
        this.logout();
        
        return throwError(() => error);
      })
    );
  }


  /**
   * Check if a refresh token request is currently in progress
   */
  isRefreshingToken(): boolean {
    return this.refreshTokenInProgress;
  }

  /**
   * Get the refresh token subject for waiting on token refresh
   */
  getRefreshTokenSubject(): BehaviorSubject<string | null> {
    return this.refreshTokenSubject;
  }
}

